import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import WayaReglasAuto from './WayaReglasAuto';
import WayaSimulador from './WayaSimulador';
import WayaBloqueosHistorial from './WayaBloqueosHistorial';
import CuitsVigilados from '../components/waya/CuitsVigilados';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Activity, Download, Loader2, CheckCircle2, XCircle, Search,
  Wifi, DatabaseZap, ArrowDownToLine, Bell, ArrowUpRight, ArrowDownLeft, ArrowRightLeft,
  FolderPlus, Save, Zap, RefreshCw, Ban, ClipboardCheck, ExternalLink, SlidersHorizontal,
  History, Layers, List, UserPlus, ArrowUpDown, AlertTriangle, LayoutGrid, Clock,
  Lock, Bot, ShieldCheck, X, Mail, MailX, FlaskConical, ShieldOff, Eye,
} from 'lucide-react';

const FASES = [
  { key: 'conectando', label: 'Conectando a Waya', icon: Wifi },
  { key: 'buscando', label: 'Buscando transacciones', icon: Search },
  { key: 'extrayendo', label: 'Extrayendo datos', icon: DatabaseZap },
];
const faseIndex = (f) => {
  const i = FASES.findIndex(x => x.key === f);
  return i === -1 ? (f === 'iniciando' ? 0 : -1) : i;
};

const ALERTA_META = {
  MONTO_MUY_PEQUEÑO: { label: 'Monto muy pequeño', cls: 'bg-red-100 text-red-700 border-red-200' },
  MONTO_PEQUEÑO: { label: 'Monto pequeño', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  NOCTURNA: { label: 'Nocturna', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  CUENTA_NUEVA: { label: 'Cuenta nueva', cls: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200' },
  CUIT_VIGILADO: { label: 'CUIT vigilado', cls: 'bg-red-600 text-white border-red-700' },
  REGLA: { label: 'Regla del motor', cls: 'bg-rose-700 text-white border-rose-800' },
};
const ALERTA_CODES = Object.keys(ALERTA_META).filter(c => c !== 'REGLA');
// Montos y nocturnidad son señales del movimiento: no llegan solas a la bandeja
const BANDEJA_CODES = ['REGLA', 'CUIT_VIGILADO', 'CUENTA_NUEVA'];
const ESTADOS = [
  { key: 'PENDIENTE', label: 'Pendientes' },
  { key: 'DETECTADA_MONITOR', label: 'Detectadas por monitor' },
  { key: 'TRABAJADA', label: 'Trabajadas' },
  { key: 'DESESTIMADA', label: 'Desestimadas' },
  { key: 'ARCHIVADA', label: 'Archivadas (+7 días)' },
  { key: 'TODOS', label: 'Todas' },
];
const ESTADO_CLS = {
  PENDIENTE: 'bg-amber-100 text-amber-700', TRABAJADA: 'bg-emerald-100 text-emerald-700',
  DESESTIMADA: 'bg-slate-200 text-slate-600', DETECTADA_MONITOR: 'bg-sky-100 text-sky-700',
  ARCHIVADA: 'bg-slate-100 text-slate-500',
};
const WAYA_PREFIX = '00003573';

const fmtMoneda = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtFecha = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const digits = (v) => String(v || '').replace(/\D/g, '');
const nowLocal = (offsetHours = 0) => {
  const d = new Date(Date.now() + offsetHours * 3600000);
  d.setSeconds(0, 0);
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const toWayaDate = (v) => {
  if (!v) return '';
  const [d, t] = v.split('T');
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y} ${t}`;
};
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const MovBadge = ({ tipo }) => (
  tipo === 'EGRESO'
    ? <span className="inline-flex items-center gap-1 text-rose-600 text-[11px] font-semibold"><ArrowUpRight size={12} /> EGRESO</span>
    : tipo === 'INTERNA'
    ? <span className="inline-flex items-center gap-1 text-amber-600 text-[11px] font-semibold"><ArrowRightLeft size={12} /> INTERNA</span>
    : <span className="inline-flex items-center gap-1 text-emerald-600 text-[11px] font-semibold"><ArrowDownLeft size={12} /> INGRESO</span>
);
const AlertaBadges = ({ alertas, esNueva, dias }) => {
  const dyn = esNueva !== undefined;
  const list = dyn ? (alertas || []).filter(a => a !== 'CUENTA_NUEVA') : (alertas || []);
  return (
    <div className="flex flex-wrap gap-1">
      {list.map(a => (
        <span key={a} className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${ALERTA_META[a]?.cls || 'bg-slate-100 text-slate-600'}`}>
          {ALERTA_META[a]?.label || a}
        </span>
      ))}
      {dyn && esNueva && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${ALERTA_META.CUENTA_NUEVA.cls}`}>
          Cuenta nueva{dias != null ? ` · ${dias}d` : ''}
        </span>
      )}
    </div>
  );
};
const SenalesBadges = ({ senales }) => (
  (senales || []).length ? (
    <div className="flex flex-wrap gap-1 mt-0.5" title="Señales del movimiento (no generan alerta por sí solas)">
      {senales.map(a => (
        <span key={a} className="text-[9px] px-1 py-0.5 rounded border border-dashed border-slate-300 text-slate-500 bg-white">{ALERTA_META[a]?.label || a}</span>
      ))}
    </div>
  ) : null
);
const ReglaBadge = ({ m }) => (
  <div className="flex flex-wrap items-center gap-1" data-testid={`waya-alerta-regla-${m.id_waya}`}>
    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${ALERTA_META.REGLA.cls}`}>{m.regla_nombre || 'Regla'}</span>
    <span className="text-[10px] text-slate-500">{(m.id_wayas || []).length} mov.</span>
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${m.accion === 'CIERRE' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
      {m.accion === 'CIERRE' ? 'Propone cierre' : (m.modo === 'EJECUTAR' ? 'Bloqueo automático' : 'Propone bloqueo')}
    </span>
  </div>
);
const AlertasCelda = ({ m }) => (
  m.tipo_alerta === 'REGLA' ? <ReglaBadge m={m} /> : (
    <><AlertaBadges alertas={m.alertas} esNueva={m.es_cuenta_nueva} dias={m.dias_antiguedad} /><SenalesBadges senales={m.senales} /></>
  )
);
const TipoBadges = ({ g }) => {
  const esNueva = g.es_cuenta_nueva != null ? g.es_cuenta_nueva : !!(g.tipos && g.tipos.CUENTA_NUEVA);
  const entries = Object.entries(g.tipos || {}).filter(([k]) => k !== 'CUENTA_NUEVA');
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${ALERTA_META[k]?.cls || 'bg-slate-100'}`}>{ALERTA_META[k]?.label || k}: {v}</span>
      ))}
      {esNueva && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${ALERTA_META.CUENTA_NUEVA.cls}`}>
          Cuenta nueva{g.dias_antiguedad != null ? ` · ${g.dias_antiguedad}d` : ''}
        </span>
      )}
    </div>
  );
};
const ExcluidaTag = ({ testid }) => (
  <span className="ml-1 inline-flex items-center gap-1 rounded bg-slate-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white align-middle" title="En la lista de exclusiones: el motor no la bloquea automáticamente" data-testid={testid}>
    <ShieldOff size={10} /> Excluida
  </span>
);
const Cuenta = ({ m }) => (
  <>{m.nombre_mov || '-'}{m.excluida && <ExcluidaTag testid={`waya-alerta-excluida-${m.id_waya}`} />}<div className="text-[10px] text-slate-400 font-mono">{m.cuit_mov || ''} · {m.cvu_mov || '-'}</div></>
);
const Mono = ({ v }) => <span className="font-mono">{v || '-'}</span>;

const MOV_COLS = [
  { key: 'id_waya', label: 'Id Waya', render: m => <Mono v={m.id_waya} /> },
  { key: 'fecha', label: 'Fecha mov.', render: m => m.fecha },
  { key: 'movimiento', label: 'Movimiento', render: m => <MovBadge tipo={m.movimiento} /> },
  { key: 'monto', label: 'Monto', align: 'right', render: m => fmtMoneda(m.monto) },
  { key: 'cuenta_mon', label: 'Cuenta monitoreada', render: m => <Cuenta m={m} /> },
  { key: 'cbu_cvu_origen', label: 'CVU/CBU origen', render: m => <Mono v={m.cbu_cvu_origen} /> },
  { key: 'cbu_cvu_destino', label: 'CVU/CBU destino', render: m => <Mono v={m.cbu_cvu_destino} /> },
  { key: 'nombre_origen', label: 'Nombre origen', render: m => m.nombre_origen || '-' },
  { key: 'nombre_destino', label: 'Nombre destino', render: m => m.nombre_destino || '-' },
  { key: 'cuit_cuil_origen', label: 'CUIT origen', render: m => <Mono v={m.cuit_cuil_origen} /> },
  { key: 'cuit_cuil_destino', label: 'CUIT destino', render: m => <Mono v={m.cuit_cuil_destino} /> },
  { key: 'banco_destinatario', label: 'Banco destinatario', render: m => m.banco_destinatario || '-' },
  { key: 'id_coelsa', label: 'Id Coelsa', render: m => <Mono v={m.id_coelsa} /> },
  { key: 'referencia', label: 'Referencia', render: m => <span className="block max-w-[180px] truncate" title={m.referencia}>{m.referencia || '-'}</span> },
  { key: 'alertas', label: 'Alertas', render: m => <><AlertaBadges alertas={m.alertas} /><SenalesBadges senales={m.senales} /></> },
];
const ALERTA_COLS = [
  { key: 'cuenta_mon', label: 'Cuenta (cvu_mov)', render: m => <Cuenta m={m} /> },
  { key: 'movimiento', label: 'Movimiento', render: m => (m.tipo_alerta === 'REGLA' ? <span className="text-[11px] text-slate-600">{(m.id_wayas || []).length} movimientos</span> : <MovBadge tipo={m.movimiento} />) },
  { key: 'monto', label: 'Monto', align: 'right', render: m => fmtMoneda(m.monto) },
  { key: 'fecha', label: 'Fecha mov.', render: m => m.fecha },
  { key: 'fecha_deteccion', label: 'Detectada', render: m => fmtFecha(m.fecha_deteccion) },
  { key: 'alertas', label: 'Alertas', render: m => <AlertasCelda m={m} /> },
  { key: 'origen', label: 'Origen', render: m => (m.origen === 'cron' ? 'Auto' : 'Manual') },
  { key: 'estado', label: 'Estado', render: m => <Badge className={ESTADO_CLS[m.estado] || ''}>{m.estado}</Badge> },
  { key: 'id_waya', label: 'Id Waya', render: m => <Mono v={m.id_waya} /> },
  { key: 'cbu_cvu_origen', label: 'CVU/CBU origen', render: m => <Mono v={m.cbu_cvu_origen} /> },
  { key: 'cbu_cvu_destino', label: 'CVU/CBU destino', render: m => <Mono v={m.cbu_cvu_destino} /> },
  { key: 'nombre_origen', label: 'Nombre origen', render: m => m.nombre_origen || '-' },
  { key: 'nombre_destino', label: 'Nombre destino', render: m => m.nombre_destino || '-' },
  { key: 'cuit_cuil_origen', label: 'CUIT origen', render: m => <Mono v={m.cuit_cuil_origen} /> },
  { key: 'cuit_cuil_destino', label: 'CUIT destino', render: m => <Mono v={m.cuit_cuil_destino} /> },
  { key: 'banco_destinatario', label: 'Banco destinatario', render: m => m.banco_destinatario || '-' },
  { key: 'id_coelsa', label: 'Id Coelsa', render: m => <Mono v={m.id_coelsa} /> },
  { key: 'referencia', label: 'Referencia', render: m => <span className="block max-w-[180px] truncate" title={m.referencia}>{m.referencia || '-'}</span> },
];
const DEFAULT_MOV = ['id_waya', 'fecha', 'movimiento', 'monto', 'cuenta_mon', 'referencia', 'alertas'];
const DEFAULT_ALERTA = ['cuenta_mon', 'movimiento', 'monto', 'fecha', 'alertas', 'origen'];

const loadLS = (key, fallback) => {
  try { const v = JSON.parse(localStorage.getItem(key)); if (v !== null && v !== undefined) return v; } catch { /* noop */ }
  return fallback;
};

const ColumnPicker = ({ catalog, visible, onToggle, testid }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" size="sm" data-testid={testid}><SlidersHorizontal size={14} className="mr-1.5" /> Columnas</Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="max-h-[60vh] overflow-auto w-60">
      <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {catalog.map(c => (
        <DropdownMenuCheckboxItem key={c.key} checked={visible.includes(c.key)}
          onCheckedChange={() => onToggle(c.key)} onSelect={(e) => e.preventDefault()}
          data-testid={`${testid}-${c.key}`}>{c.label}</DropdownMenuCheckboxItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const SortTh = ({ label, col, orden, onSort, align }) => (
  <th className={`px-3 py-2 font-semibold whitespace-nowrap cursor-pointer select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
    onClick={() => onSort(col)} data-testid={`waya-grp-sort-${col}`}>
    <span className="inline-flex items-center gap-1">{label}
      <ArrowUpDown size={11} className={orden.col === col ? 'text-orange-600' : 'text-slate-300'} />
    </span>
  </th>
);

const WayaMovimientos = () => {
  const navigate = useNavigate();
  const [desde, setDesde] = useState(nowLocal(-24));
  const [hasta, setHasta] = useState(nowLocal(0));
  const [loading, setLoading] = useState(false);
  const [fase, setFase] = useState(null);
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [tab, setTab] = useState('alertas'); // alertas | movimientos | historial

  const [config, setConfig] = useState(null);
  const [runningNow, setRunningNow] = useState(false);

  // Permisos de acciones (crear / ejecutar) sobre el Monitor Waya
  const [perm, setPerm] = useState({ crear: true, ejecutar: true, admin: false });
  const [permDlgOpen, setPermDlgOpen] = useState(false);
  const [permUsers, setPermUsers] = useState([]);
  const [permLoading, setPermLoading] = useState(false);

  const [bandeja, setBandeja] = useState({ items: [], pendientes: 0, resumen_alertas: {} });
  const [bandejaLoading, setBandejaLoading] = useState(false);
  const [estadoFiltro, setEstadoFiltro] = useState('PENDIENTE');
  const [rangoHoras, setRangoHoras] = useState(0); // 0 = Todo · 24 · 48 · 168 (7d)
  const [filtroTipos, setFiltroTipos] = useState([]);
  const [filtroDir, setFiltroDir] = useState('TODOS');
  const [seleccion, setSeleccion] = useState(new Set());
  const [alertaVista, setAlertaVista] = useState('detalle'); // detalle | agrupado

  // Agrupado
  const [grupos, setGrupos] = useState([]);
  const [gruposLoading, setGruposLoading] = useState(false);
  const [excluidasOcultas, setExcluidasOcultas] = useState(0);
  const [mostrarExcluidas, setMostrarExcluidas] = useState(true);
  const [agrupadoPor, setAgrupadoPor] = useState('cvu'); // cvu | cuit
  const [orden, setOrden] = useState({ col: 'total_count', dir: 'desc' });
  const [selGrupos, setSelGrupos] = useState(new Set());
  const [padronLoading, setPadronLoading] = useState(false);
  const [nuevasLoading, setNuevasLoading] = useState(false);
  const [umbralMov, setUmbralMov] = useState(() => Number(loadLS('waya_umbral_mov', 5)));
  const [umbralMonto, setUmbralMonto] = useState(() => Number(loadLS('waya_umbral_monto', 0)));
  useEffect(() => { localStorage.setItem('waya_umbral_mov', JSON.stringify(umbralMov)); }, [umbralMov]);
  useEffect(() => { localStorage.setItem('waya_umbral_monto', JSON.stringify(umbralMonto)); }, [umbralMonto]);

  // Historial
  const [historial, setHistorial] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);

  // Columnas
  const [movCols, setMovCols] = useState(() => loadLS('waya_cols_mov', DEFAULT_MOV));
  const [alertaCols, setAlertaCols] = useState(() => loadLS('waya_cols_alertas', DEFAULT_ALERTA));
  useEffect(() => { localStorage.setItem('waya_cols_mov', JSON.stringify(movCols)); }, [movCols]);
  useEffect(() => { localStorage.setItem('waya_cols_alertas', JSON.stringify(alertaCols)); }, [alertaCols]);
  const toggleCol = (setter) => (key) => setter(prev => prev.includes(key) ? (prev.length > 1 ? prev.filter(k => k !== key) : prev) : [...prev, key]);
  const movVisible = useMemo(() => MOV_COLS.filter(c => movCols.includes(c.key)), [movCols]);
  const alertaVisible = useMemo(() => ALERTA_COLS.filter(c => alertaCols.includes(c.key)), [alertaCols]);

  const [genOpen, setGenOpen] = useState(false);
  const [genAgrup, setGenAgrup] = useState('cvu');
  const [genPrioridad, setGenPrioridad] = useState('MEDIA');
  const [genTitulo, setGenTitulo] = useState('');
  const [genArrastrar, setGenArrastrar] = useState(true);
  const [genLoading, setGenLoading] = useState(false);

  // Selección de tarjetas del Muro + bloqueo/cierre definitivo
  const [selMuro, setSelMuro] = useState(new Set());
  const [bloqOpen, setBloqOpen] = useState(false);
  const [bloqTipo, setBloqTipo] = useState('preventivo');
  const [bloqPrioridad, setBloqPrioridad] = useState('MEDIA');
  const [bloqIds, setBloqIds] = useState([]);
  const [bloqLoading, setBloqLoading] = useState(false);

  // Modal "Ver movimientos" de una cuenta (Muro)
  const [movModalOpen, setMovModalOpen] = useState(false);
  const [movModalGrupo, setMovModalGrupo] = useState(null);
  const [movModalItems, setMovModalItems] = useState([]);
  const [movModalLoading, setMovModalLoading] = useState(false);
  const [movModalSel, setMovModalSel] = useState(new Set());

  // Propuestas del motor automático (Fase 2 en el Muro)
  const [propuestas, setPropuestas] = useState({});     // { cvu: {accion, modo, reglas, cant_movimientos} }
  const [motorActivo, setMotorActivo] = useState(false);
  const [motorApply, setMotorApply] = useState(null);   // { cvu, verdict } | null
  const [motorLoading, setMotorLoading] = useState(false);

  // Job de bloqueo/cierre en vivo (banner arriba de "Alertas en Vivo")
  const [bloqJob, setBloqJob] = useState(null);
  const bloqJobTimer = useRef(null);

  const activeIdx = useMemo(() => faseIndex(fase), [fase]);

  const fetchConfig = useCallback(async () => {
    try { const r = await api.get('/waya-movimientos/config'); setConfig(r.data); } catch { /* noop */ }
  }, []);
  const fetchBandeja = useCallback(async () => {
    setBandejaLoading(true);
    try {
      const params = { estado: estadoFiltro, limit: 1000 };
      if (filtroDir !== 'TODOS') params.direccion = filtroDir;
      if (rangoHoras > 0) params.horas = rangoHoras;
      const r = await api.get('/waya-movimientos/alertas', { params });
      setBandeja(r.data || { items: [], pendientes: 0, resumen_alertas: {} });
      setSeleccion(new Set());
    } catch { /* noop */ } finally { setBandejaLoading(false); }
  }, [estadoFiltro, filtroDir, rangoHoras]);
  const fetchGrupos = useCallback(async () => {
    setGruposLoading(true);
    try {
      const r = await api.get('/waya-movimientos/alertas/agrupado', { params: { por: agrupadoPor, estado: estadoFiltro, limit: 1000, incluir_excluidas: mostrarExcluidas, ...(rangoHoras > 0 ? { horas: rangoHoras } : {}) } });
      setGrupos(r.data?.grupos || []); setSelGrupos(new Set());
      setExcluidasOcultas(r.data?.excluidas_ocultas || 0);
    } catch { /* noop */ } finally { setGruposLoading(false); }
  }, [agrupadoPor, estadoFiltro, rangoHoras, mostrarExcluidas]);
  const fetchHistorial = useCallback(async () => {
    setHistorialLoading(true);
    try { const r = await api.get('/waya-movimientos/ejecuciones', { params: { limit: 200 } }); setHistorial(r.data?.items || []); }
    catch { /* noop */ } finally { setHistorialLoading(false); }
  }, []);
  const fetchPropuestas = useCallback(async () => {
    try {
      const r = await api.get('/waya-auto/propuestas');
      setPropuestas(r.data?.propuestas || {});
      setMotorActivo(!!r.data?.activo);
    } catch { setPropuestas({}); }
  }, []);

  const aplicarMotor = async () => {
    if (!motorApply) return;
    setMotorLoading(true);
    try {
      const r = await api.post('/waya-auto/ejecutar', { cvus: [motorApply.cvu] });
      toast.success(`Motor aplicado: ${r.data.bloqueos || 0} bloqueo(s), ${r.data.cierres || 0} cierre(s)`);
      setMotorApply(null);
      fetchGrupos(); fetchPropuestas();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo aplicar'); }
    finally { setMotorLoading(false); }
  };

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => {
    api.get('/waya-movimientos/permisos/me')
      .then(r => setPerm({ crear: !!r.data?.crear, ejecutar: !!r.data?.ejecutar, admin: !!r.data?.admin }))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === 'alertas') { (alertaVista === 'agrupado' || alertaVista === 'muro') ? fetchGrupos() : fetchBandeja(); }
    if (tab === 'alertas' && (alertaVista === 'agrupado' || alertaVista === 'muro')) fetchPropuestas();
    if (tab === 'historial') fetchHistorial();
  }, [tab, alertaVista, estadoFiltro, filtroDir, agrupadoPor, fetchBandeja, fetchGrupos, fetchHistorial, fetchPropuestas]);

  const bandejaFiltrada = useMemo(() => {
    if (!filtroTipos.length) return bandeja.items;
    return bandeja.items.filter(m => filtroTipos.some(t => (m.alertas || []).includes(t)));
  }, [bandeja.items, filtroTipos]);

  const gruposOrdenados = useMemo(() => {
    const arr = [...grupos];
    const { col, dir } = orden;
    arr.sort((a, b) => {
      const va = a[col] ?? 0, vb = b[col] ?? 0;
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [grupos, orden]);
  const onSort = (col) => setOrden(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' });
  const esSospechoso = (g) => (g.total_count >= umbralMov) || (umbralMonto > 0 && g.total_monto >= umbralMonto);
  // Muro: tarjetas ordenadas por cantidad de alertas activas (alertados) del cliente
  const gruposMuro = useMemo(() => [...grupos].sort((a, b) => (b.alertados_count - a.alertados_count) || (b.total_count - a.total_count)), [grupos]);

  const extraer = async () => {
    if (!desde || !hasta) { toast.error('Elegí el rango de fechas'); return; }
    setLoading(true); setItems([]); setResumen(null); setFase('iniciando');
    try {
      const res = await api.post('/waya-movimientos/extraer', { fecha_desde: toWayaDate(desde), fecha_hasta: toWayaDate(hasta) });
      const jid = res.data?.job_id; let job = null;
      for (let i = 0; i < 400; i++) {
        await sleep(2500);
        const jr = await api.get(`/waya-movimientos/job/${jid}`); job = jr.data;
        setFase(job?.fase || 'procesando');
        if (job?.estado === 'completado' || job?.estado === 'error') break;
      }
      if (job?.estado === 'error') { toast.error(`Error: ${job?.error || 'desconocido'}`); setFase('error'); }
      else {
        const r = job?.resultado || {};
        setItems(r.items || []); setResumen(r);
        const g = r.guardado || {};
        toast.success(`${r.total || 0} movimiento(s) · ${g.guardados || 0} guardado(s) · ${r.total_alertadas || 0} alerta(s)`);
        if (tab === 'alertas') { (alertaVista === 'agrupado' || alertaVista === 'muro') ? fetchGrupos() : fetchBandeja(); }
        if ((r.total_alertadas || 0) > 0) setTab('alertas');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al extraer de Waya'); setFase('error'); }
    finally { setLoading(false); }
  };

  const toggleAuto = async (val) => {
    try { const r = await api.put('/waya-movimientos/config', { enabled: val }); setConfig(r.data); toast.success(val ? 'Automatización ACTIVADA (cada 1 hora)' : 'Automatización desactivada'); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cambiar'); }
  };
  const runNow = async () => {
    setRunningNow(true);
    try {
      await api.post('/waya-movimientos/run-now');
      toast.success('Extracción automática lanzada. Refrescá en ~1 minuto.');
      setTimeout(() => { fetchConfig(); if (tab === 'historial') fetchHistorial(); }, 45000);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo ejecutar'); }
    finally { setTimeout(() => setRunningNow(false), 3000); }
  };

  const toggleTipo = (t) => setFiltroTipos(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const toggleSel = (id) => setSeleccion(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelAll = () => {
    const vis = bandejaFiltrada.map(m => m.id_waya);
    const all = vis.length > 0 && vis.every(id => seleccion.has(id));
    setSeleccion(prev => { const n = new Set(prev); all ? vis.forEach(id => n.delete(id)) : vis.forEach(id => n.add(id)); return n; });
  };
  const toggleSelGrupo = (cvu) => setSelGrupos(prev => { const n = new Set(prev); n.has(cvu) ? n.delete(cvu) : n.add(cvu); return n; });

  const resolverIds = async (ids, accion) => {
    const lista = (ids || []).filter(Boolean);
    if (!lista.length) { toast.error('Seleccioná al menos una alerta'); return; }
    try {
      const r = await api.post('/waya-movimientos/alertas/resolver', { id_wayas: lista, accion });
      toast.success(`${r.data?.actualizadas || 0} alerta(s) ${accion === 'desestimar' ? 'desestimada(s)' : 'marcada(s) como trabajada(s)'}`);
      setSeleccion(new Set());
      (alertaVista === 'agrupado' || alertaVista === 'muro') ? fetchGrupos() : fetchBandeja();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo actualizar'); }
  };
  const resolver = (accion) => resolverIds(Array.from(seleccion), accion);

  const abrirGenerar = () => {
    if (seleccion.size === 0) { toast.error('Seleccioná al menos una alerta'); return; }
    setGenTitulo(''); setGenAgrup('cvu'); setGenPrioridad('MEDIA'); setGenArrastrar(true); setGenOpen(true);
  };
  const generarDesdeGrupo = (g) => {
    const ids = (g.id_wayas || []).filter(Boolean);
    if (!ids.length) { toast.error('La cuenta no tiene alertas pendientes'); return; }
    setSeleccion(new Set(ids));
    setGenTitulo(''); setGenAgrup('cvu'); setGenPrioridad('MEDIA'); setGenArrastrar(true); setGenOpen(true);
  };
  const confirmarGenerar = async () => {
    setGenLoading(true);
    try {
      const res = await api.post('/waya-movimientos/alertas/generar-caso', {
        id_wayas: Array.from(seleccion), agrupacion: genAgrup, prioridad: genPrioridad,
        estado: 'ABIERTO', titulo: genAgrup === 'uno' ? (genTitulo || null) : null,
        arrastrar_cuenta: genArrastrar,
      });
      const casos = res.data?.casos || [];
      toast.success(`${res.data?.casos_creados || 0} caso(s) creado(s)`);
      setGenOpen(false); setSeleccion(new Set());
      (alertaVista === 'agrupado' || alertaVista === 'muro') ? fetchGrupos() : fetchBandeja();
      if (casos.length === 1) navigate(`/m/gestion-de-casos/caso/${casos[0].id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo generar el caso'); }
    finally { setGenLoading(false); }
  };

  // ---- Selección múltiple (Muro / Agrupado) + Bloqueo ----
  const toggleSelMuro = (clave) => setSelMuro(prev => { const n = new Set(prev); n.has(clave) ? n.delete(clave) : n.add(clave); return n; });
  const idsFromGrupos = (arr, sel, keyField) => {
    const ids = [];
    (arr || []).forEach(g => { if (sel.has(g[keyField])) (g.id_wayas || []).forEach(id => id && ids.push(id)); });
    return Array.from(new Set(ids));
  };
  const idsFromMuro = () => idsFromGrupos(gruposMuro, selMuro, 'clave');
  const generarDesdeSeleccionMuro = () => {
    const ids = idsFromMuro();
    if (!ids.length) { toast.error('Seleccioná al menos una tarjeta'); return; }
    setSeleccion(new Set(ids));
    setGenTitulo(''); setGenAgrup('cvu'); setGenPrioridad('MEDIA'); setGenArrastrar(true); setGenOpen(true);
  };
  const generarDesdeSelGrupos = () => {
    const ids = idsFromGrupos(gruposOrdenados, selGrupos, 'cvu_mov');
    if (!ids.length) { toast.error('Seleccioná al menos una cuenta'); return; }
    setSeleccion(new Set(ids));
    setGenTitulo(''); setGenAgrup('cvu'); setGenPrioridad('MEDIA'); setGenArrastrar(true); setGenOpen(true);
  };
  const abrirBloqueo = (ids) => {
    const lista = (ids || []).filter(Boolean);
    if (!lista.length) { toast.error('Seleccioná al menos una alerta/cuenta'); return; }
    setBloqIds(lista); setBloqTipo('preventivo'); setBloqPrioridad('MEDIA'); setBloqOpen(true);
  };
  const _pollBloqueoJob = async (jid) => {
    if (bloqJobTimer.current) { clearTimeout(bloqJobTimer.current); bloqJobTimer.current = null; }
    for (let i = 0; i < 150; i++) {
      try {
        const jr = await api.get(`/waya-movimientos/alertas/bloquear-generar/${jid}`);
        setBloqJob(jr.data);
        if (jr.data?.estado === 'completado') {
          const r = jr.data.resultado || {};
          const accion = r.tipo === 'preventivo'
            ? `${r.bloqueadas || 0} cuenta(s) bloqueada(s)`
            : `${r.bajas || 0} CVU(s) dado(s) de baja`;
          toast.success(`Waya: ${accion} · ${r.emails || 0} email(s) enviado(s)`);
          bloqJobTimer.current = setTimeout(() => setBloqJob(null), 60000); // auto-oculta a los 60s
          return;
        }
      } catch { return; }
      await sleep(4000);
    }
  };
  const excluirCuentas = async (cuentas) => {
    const lista = (cuentas || []).filter(c => c && (c.cvu || c.cuit));
    if (!lista.length) { toast.error('Sin cuentas para excluir'); return; }
    try {
      const r = await api.post('/waya-auto/exclusiones/bulk', { cuentas: lista, por_cvu: true, por_cuit: true });
      toast.success(`${r.data?.creadas || 0} exclusión(es) creada(s) (CVU + CUIT)`);
      setSelMuro(new Set());
      fetchGrupos();
      fetchPropuestas();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo excluir'); }
  };
  const confirmarBloqueo = async () => {
    setBloqLoading(true);
    try {
      const res = await api.post('/waya-movimientos/alertas/bloquear-generar', {
        id_wayas: bloqIds, tipo: bloqTipo, agrupacion: 'cvu',
        prioridad: bloqPrioridad, arrastrar_cuenta: true,
      });
      toast.success(`${res.data?.casos_creados || 0} caso(s) creado(s) (${bloqTipo === 'preventivo' ? 'Esperando Compliance' : 'Cerrado'}). Ejecutando en Waya y enviando email…`);
      setBloqOpen(false); setSeleccion(new Set()); setSelMuro(new Set());
      (alertaVista === 'agrupado' || alertaVista === 'muro') ? fetchGrupos() : fetchBandeja();
      if (res.data?.job_id) {
        setBloqJob({ id: res.data.job_id, estado: 'procesando', tipo: bloqTipo,
                     total: res.data?.casos_creados || 0, progreso: {}, resultado: null });
        _pollBloqueoJob(res.data.job_id);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo procesar el bloqueo'); }
    finally { setBloqLoading(false); }
  };

  // ---- Modal "Ver movimientos" de una cuenta (Muro) ----
  const abrirMovModal = async (g) => {
    setMovModalGrupo(g); setMovModalItems([]); setMovModalSel(new Set()); setMovModalOpen(true);
    setMovModalLoading(true);
    try {
      const params = { estado: estadoFiltro, cvu: g.cvu_mov, limit: 1000 };
      if (rangoHoras > 0) params.horas = rangoHoras;
      const r = await api.get('/waya-movimientos/alertas', { params });
      setMovModalItems(r.data?.items || []);
    } catch { toast.error('No se pudieron cargar los movimientos'); }
    finally { setMovModalLoading(false); }
  };
  const toggleMovSel = (id) => setMovModalSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleMovSelAll = () => {
    const vis = movModalItems.map(m => m.id_waya);
    const all = vis.length > 0 && vis.every(id => movModalSel.has(id));
    setMovModalSel(all ? new Set() : new Set(vis));
  };
  const modalGenerar = () => {
    const ids = Array.from(movModalSel);
    if (!ids.length) { toast.error('Seleccioná al menos un movimiento'); return; }
    setSeleccion(new Set(ids));
    setGenTitulo(''); setGenAgrup('cvu'); setGenPrioridad('MEDIA'); setGenArrastrar(false);
    setMovModalOpen(false); setGenOpen(true);
  };
  const modalBloquear = () => {
    const ids = Array.from(movModalSel);
    if (!ids.length) { toast.error('Seleccioná al menos un movimiento'); return; }
    setMovModalOpen(false); abrirBloqueo(ids);
  };
  const modalDesestimar = async () => {
    const ids = Array.from(movModalSel);
    if (!ids.length) { toast.error('Seleccioná al menos un movimiento'); return; }
    await resolverIds(ids, 'desestimar');
    if (movModalGrupo) abrirMovModal(movModalGrupo);
  };

  // ---- Admin: gestión de permisos de acciones por usuario ----
  const abrirPermisos = async () => {
    setPermDlgOpen(true); setPermLoading(true);
    try { const r = await api.get('/waya-movimientos/permisos/usuarios'); setPermUsers(r.data?.usuarios || []); }
    catch { toast.error('No se pudieron cargar los permisos'); }
    finally { setPermLoading(false); }
  };
  const togglePermUser = async (u, campo, valor) => {
    const nuevo = { crear: u.crear, ejecutar: u.ejecutar, [campo]: valor };
    setPermUsers(prev => prev.map(x => x.id === u.id ? { ...x, ...nuevo } : x));
    try {
      await api.put(`/waya-movimientos/permisos/usuarios/${u.id}`, nuevo);
      toast.success(`Permisos actualizados: ${u.nombre}`);
    } catch {
      toast.error('No se pudo guardar');
      setPermUsers(prev => prev.map(x => x.id === u.id ? { ...x, [campo]: !valor } : x));
    }
  };

  const agregarPadron = async (cvus) => {
    const lista = (cvus || []).filter(Boolean);
    if (!lista.length) { toast.error('Seleccioná al menos una cuenta'); return; }
    setPadronLoading(true);
    try {
      const res = await api.post('/waya-movimientos/alertas/agregar-padron', { cvus: lista });
      const jid = res.data?.job_id; let job = null;
      for (let i = 0; i < 40; i++) {
        await sleep(4000);
        const jr = await api.get(`/waya-movimientos/alertas/agregar-padron/${jid}`); job = jr.data;
        if (job?.estado === 'completado' || job?.estado === 'error') break;
      }
      if (job?.estado === 'error') { toast.error('Error al agregar al padrón'); }
      else {
        const rs = job?.resultados || [];
        const c = (e) => rs.filter(r => r.estado === e).length;
        toast.success(`Padrón: ${c('agregada')} agregada(s), ${c('ya_existia')} ya existían, ${c('no_encontrada_waya')} no encontradas, ${c('omitida_no_waya')} externas omitidas`);
        setSelGrupos(new Set()); fetchGrupos();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo agregar al padrón'); }
    finally { setPadronLoading(false); }
  };

  const _pollPadronJob = async (jid) => {
    let job = null;
    for (let i = 0; i < 90; i++) {
      await sleep(4000);
      const jr = await api.get(`/waya-movimientos/alertas/agregar-padron/${jid}`); job = jr.data;
      if (job?.estado === 'completado' || job?.estado === 'error') break;
    }
    return job;
  };

  const descargarCsvNoEnc = async (jid) => {
    try {
      const resp = await api.get(`/waya-movimientos/alertas/agregar-padron/${jid}/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `cuentas_no_encontradas_${jid.slice(0, 8)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };

  const _resumenPadron = (job, jid) => {
    const rs = job?.resultados || [];
    const c = (e) => rs.filter(r => r.estado === e).length;
    toast.success(`Padrón: ${c('agregada')} agregadas · ${c('actualizada')} actualizadas · ${c('ya_existia')} ya existían · ${c('no_encontrada_waya')} no encontradas`);
    if (c('no_encontrada_waya') > 0) descargarCsvNoEnc(jid);
  };

  const detectarNuevas = async () => {
    setNuevasLoading(true);
    try {
      const res = await api.post('/waya-movimientos/cuentas-nuevas/detectar');
      if (!res.data?.job_id) { toast.info(res.data?.message || 'No hay cuentas nuevas para registrar'); return; }
      toast.info(`Detectadas ${res.data.total} cuentas nuevas. Buscando en Waya…`);
      const job = await _pollPadronJob(res.data.job_id);
      if (job?.estado === 'error') { toast.error('Error al registrar cuentas nuevas'); return; }
      _resumenPadron(job, res.data.job_id);
      fetchGrupos();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo detectar cuentas nuevas'); }
    finally { setNuevasLoading(false); }
  };

  const barridaInicial = async () => {
    setNuevasLoading(true);
    try {
      const cnt = await api.post('/waya-movimientos/cuentas-nuevas/barrida-inicial', { solo_contar: true });
      const pend = cnt.data?.pendientes || 0;
      if (!pend) { toast.info('El padrón ya está al día (sin cuentas nuevas)'); return; }
      if (!window.confirm(`Hay ${pend} cuentas de Waya fuera del padrón. Se procesará una tanda de hasta 200 buscándolas en Waya (puede tardar varios minutos). ¿Continuar?`)) return;
      const res = await api.post('/waya-movimientos/cuentas-nuevas/barrida-inicial', { limite: 200 });
      if (!res.data?.job_id) { toast.info(res.data?.message || 'Nada para procesar'); return; }
      toast.info(`Procesando ${res.data.total} cuentas (quedan ${res.data.pendientes_restantes} para próximas tandas)…`);
      const job = await _pollPadronJob(res.data.job_id);
      if (job?.estado === 'error') { toast.error('Error en la barrida inicial'); return; }
      _resumenPadron(job, res.data.job_id);
      if (res.data.pendientes_restantes > 0) toast.info(`Quedan ${res.data.pendientes_restantes} cuentas. Repetí la barrida para procesarlas.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo hacer la barrida inicial'); }
    finally { setNuevasLoading(false); }
  };

  const igualarFechasAlta = async () => {
    if (!window.confirm('Puesta al día por única vez: completa la fecha de creación en las cuentas que ya tienen fecha de alta registrada (no pisa datos). ¿Continuar?')) return;
    setNuevasLoading(true);
    try {
      const res = await api.post('/waya-movimientos/padron/backfill-fecha-creacion');
      toast.success(`Fechas de alta igualadas: ${res.data.actualizados} de ${res.data.revisados} cuentas actualizadas`);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo igualar las fechas de alta'); }
    finally { setNuevasLoading(false); }
  };

  const exportCSV = () => {
    if (!items.length) return;
    const cols = MOV_COLS.map(c => c.key);
    const val = (m, k) => k === 'cuenta_mon' ? `${m.nombre_mov || ''} ${m.cuit_mov || ''} ${m.cvu_mov || ''}`.trim() : (Array.isArray(m[k]) ? m[k].join('|') : (m[k] ?? ''));
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [cols.join(','), ...items.map(m => cols.map(c => esc(val(m, c))).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `waya_movimientos_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const guardado = resumen?.guardado || {};
  const selGruposWaya = useMemo(() => Array.from(selGrupos).filter(c => digits(c).startsWith(WAYA_PREFIX)), [selGrupos]);

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="waya-mov-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity size={28} className="text-emerald-600" /> Monitor de Movimientos Waya
          </h1>
          <p className="text-slate-600 mt-1 text-sm">Extracción de Waya, guardado en base, alertas en vivo con bandeja de trabajo y detección de acumulación.</p>
        </div>
        <div className="flex items-center gap-2">
          {perm.admin && (
            <Button variant="outline" onClick={abrirPermisos} data-testid="waya-permisos-btn"><Lock size={16} className="mr-2" /> Permisos</Button>
          )}
          {items.length > 0 && (
            <Button variant="outline" onClick={exportCSV} data-testid="waya-mov-export-btn"><Download size={16} className="mr-2" /> Exportar CSV</Button>
          )}
        </div>
      </div>

      {/* Barra de automatización */}
      <Card className="p-4 bg-white mb-4" data-testid="waya-auto-bar">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            {perm.ejecutar
              ? <Switch checked={!!config?.enabled} onCheckedChange={toggleAuto} data-testid="waya-auto-switch" />
              : <Lock size={18} className="text-slate-300" />}
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Zap size={15} className={config?.enabled ? 'text-emerald-600' : 'text-slate-400'} />
                Automatización cada 1 hora
                <Badge className={config?.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>{config?.enabled ? 'ACTIVA' : 'APAGADA'}</Badge>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">Extrae la última hora (colchón {config?.colchon_min || 75} min) y actualiza la bandeja.</p>
            </div>
          </div>
          <div className="text-xs text-slate-600 border-l pl-4">
            <div>Última corrida: <strong>{fmtFecha(config?.last_run_at)}</strong>
              {config?.last_status && <span className={`ml-1 ${config.last_status === 'ok' ? 'text-emerald-600' : 'text-red-600'}`}>({config.last_status === 'ok' ? 'OK' : 'error'})</span>}
            </div>
            {config?.last_run_at && <div className="text-slate-400">{config.last_extraidos || 0} mov · {config.last_alertadas || 0} alertas</div>}
          </div>
          {perm.ejecutar && (
            <Button variant="outline" size="sm" onClick={runNow} disabled={runningNow} className="ml-auto" data-testid="waya-run-now-btn">
              {runningNow ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Zap size={14} className="mr-2" />} Ejecutar ahora
            </Button>
          )}
        </div>
      </Card>

      {/* Extracción manual */}
      {perm.ejecutar && (
      <Card className="p-4 bg-white mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div><Label>Desde</Label><Input type="datetime-local" value={desde} onChange={e => setDesde(e.target.value)} className="mt-1" data-testid="waya-mov-desde" disabled={loading} /></div>
          <div><Label>Hasta</Label><Input type="datetime-local" value={hasta} onChange={e => setHasta(e.target.value)} className="mt-1" data-testid="waya-mov-hasta" disabled={loading} /></div>
          <Button onClick={extraer} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="waya-mov-extraer-btn">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ArrowDownToLine size={16} className="mr-2" />}{loading ? 'Extrayendo…' : 'Extraer de Waya'}
          </Button>
        </div>
      </Card>
      )}

      {(loading || fase) && fase !== 'completado' && fase !== 'error' && (
        <Card className="p-4 bg-white mb-4" data-testid="waya-mov-progreso">
          <div className="flex items-center gap-3 flex-wrap">
            {FASES.map((f, idx) => {
              const Icon = f.icon; const done = activeIdx > idx; const active = activeIdx === idx;
              return (
                <React.Fragment key={f.key}>
                  <div className={`flex items-center gap-2 text-sm ${active ? 'text-emerald-700 font-semibold' : done ? 'text-emerald-500' : 'text-slate-400'}`}>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center ${active ? 'bg-emerald-100' : done ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                      {active ? <Loader2 size={16} className="animate-spin" /> : done ? <CheckCircle2 size={16} /> : <Icon size={16} />}
                    </span>{f.label}
                  </div>
                  {idx < 2 && <div className={`h-0.5 w-8 ${done ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </Card>
      )}

      {fase === 'error' && (
        <Card className="p-4 bg-red-50 border-red-200 mb-4 flex items-center gap-2 text-red-700" data-testid="waya-mov-error">
          <XCircle size={18} /> Ocurrió un error al conectar/extraer de Waya. Reintentá con un rango más corto en unos minutos.
        </Card>
      )}

      {resumen && (
        <>
          <div className="flex flex-wrap gap-2 mb-2" data-testid="waya-mov-resumen">
            <Badge className="bg-slate-100 text-slate-700 text-sm px-3 py-1">Movimientos: {resumen.total}</Badge>
            <Badge className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1">Monto total: {fmtMoneda(resumen.monto_total)}</Badge>
            <Badge className="bg-orange-100 text-orange-800 text-sm px-3 py-1">Alertadas: {resumen.total_alertadas || 0}</Badge>
            {ALERTA_CODES.map(c => (
              <Badge key={c} className={`text-sm px-3 py-1 border ${ALERTA_META[c].cls}`} data-testid={`waya-kpi-${c}`}>{ALERTA_META[c].label}: {resumen.resumen_alertas?.[c] || 0}</Badge>
            ))}
          </div>
          <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5 mb-3 flex items-start gap-2" data-testid="waya-mov-guardado">
            <Save size={15} className="text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-xs text-emerald-800">
              {guardado.disponible === false ? 'MySQL no está disponible: los movimientos no se guardaron.'
                : <>Guardado como <strong>waya-guard</strong>: <strong>{guardado.guardados || 0}</strong> nuevo(s), {guardado.ya_existian || 0} ya existían.{resumen.truncado ? ` (Se muestran ${resumen.mostrados} de ${resumen.total}; todos quedaron en base.)` : ''}</>}
            </p>
          </div>
        </>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-3" data-testid="waya-mov-tabs">
        <Button variant={tab === 'alertas' ? 'default' : 'outline'} size="sm" onClick={() => setTab('alertas')} data-testid="waya-tab-alertas">
          <Bell size={14} className="mr-1.5" /> Alertas en Vivo ({bandeja.pendientes})
        </Button>
        <Button variant={tab === 'movimientos' ? 'default' : 'outline'} size="sm" onClick={() => setTab('movimientos')} data-testid="waya-tab-movimientos">
          Movimientos {items.length > 0 ? `(${items.length})` : ''}
        </Button>
        <Button variant={tab === 'historial' ? 'default' : 'outline'} size="sm" onClick={() => setTab('historial')} data-testid="waya-tab-historial">
          <History size={14} className="mr-1.5" /> Historial
        </Button>
        <Button variant={tab === 'auto' ? 'default' : 'outline'} size="sm" onClick={() => setTab('auto')} data-testid="waya-tab-auto">
          <Bot size={14} className="mr-1.5" /> Reglas automáticas
        </Button>
        <Button variant={tab === 'bloqueos' ? 'default' : 'outline'} size="sm" onClick={() => setTab('bloqueos')} data-testid="waya-tab-bloqueos">
          <Ban size={14} className="mr-1.5" /> Bloqueos y cierres
        </Button>
        <Button variant={tab === 'vigilados' ? 'default' : 'outline'} size="sm" onClick={() => setTab('vigilados')} data-testid="waya-tab-vigilados">
          <Eye size={14} className="mr-1.5" /> CUITs vigilados
        </Button>
        <Button variant={tab === 'simulador' ? 'default' : 'outline'} size="sm" onClick={() => setTab('simulador')} data-testid="waya-tab-simulador">
          <FlaskConical size={14} className="mr-1.5" /> Simulador
        </Button>
      </div>

      {/* Reglas automáticas */}
      {tab === 'auto' && <WayaReglasAuto />}

      {/* Bloqueos y cierres ejecutados */}
      {tab === 'bloqueos' && <WayaBloqueosHistorial />}

      {/* CUITs vigilados (lista de vigilancia) */}
      {tab === 'vigilados' && <CuitsVigilados />}

      {/* Simulador (no guarda nada) */}
      {tab === 'simulador' && <WayaSimulador />}

      {/* Movimientos */}
      {tab === 'movimientos' && (items.length > 0 ? (
        <>
          <div className="flex justify-end mb-2"><ColumnPicker catalog={MOV_COLS} visible={movCols} onToggle={toggleCol(setMovCols)} testid="waya-cols-mov" /></div>
          <Card className="bg-white overflow-hidden" data-testid="waya-mov-tabla">
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                  <tr>{movVisible.map(c => <th key={c.key} className={`px-3 py-2 font-semibold whitespace-nowrap ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((m, idx) => (
                    <tr key={`${m.id_waya}-${idx}`} className="hover:bg-slate-50" data-testid={`waya-mov-row-${idx}`}>
                      {movVisible.map(c => <td key={c.key} className={`px-3 py-2 text-slate-700 ${c.align === 'right' ? 'text-right font-semibold whitespace-nowrap' : ''}`}>{c.render(m)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        <Card className="p-10 bg-white text-center text-slate-400" data-testid="waya-mov-empty">
          <Activity size={36} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm">Elegí un rango y presioná "Extraer de Waya" para ver los movimientos de esa extracción.</p>
        </Card>
      ))}

      {/* Alertas en Vivo */}
      {tab === 'alertas' && (
        <div data-testid="waya-alertas-section">
          {bloqJob && (() => {
            const pasos = Object.values(bloqJob.progreso || {});
            const done = bloqJob.estado === 'completado' ? (bloqJob.total || pasos.length) : pasos.filter(p => p.estado === 'done').length;
            const pct = bloqJob.total ? Math.round((done / bloqJob.total) * 100) : (bloqJob.estado === 'completado' ? 100 : 5);
            const fallos = (bloqJob.resultado?.emails_detalle || []).filter(e => e.estado !== 'ok');
            return (
              <Card className={`p-3 mb-3 border-l-4 ${bloqJob.estado === 'completado' ? 'border-l-emerald-500 bg-emerald-50/40' : 'border-l-sky-500 bg-sky-50/40'}`} data-testid="waya-bloq-job-banner">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {bloqJob.estado === 'completado' ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Loader2 size={16} className="text-sky-600 animate-spin" />}
                    <div>
                      <div className="text-sm font-semibold text-slate-800">
                        {bloqJob.tipo === 'definitivo' ? 'Cierre definitivo (baja de CVU)' : 'Bloqueo preventivo'} — {bloqJob.estado === 'completado' ? 'Completado' : 'En curso…'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {done}/{bloqJob.total || 0} caso(s) procesado(s)
                        {bloqJob.estado === 'completado' && bloqJob.resultado && ` · ${bloqJob.resultado.emails || 0} email(s) enviado(s)`}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => { if (bloqJobTimer.current) clearTimeout(bloqJobTimer.current); setBloqJob(null); }} className="text-slate-400 hover:text-slate-700" data-testid="waya-bloq-job-close"><X size={16} /></button>
                </div>
                <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-sky-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
                {pasos.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-auto">
                    {pasos.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`waya-bloq-job-row-${i}`}>
                        <span className="font-medium text-slate-700 truncate flex-1" title={p.titulo || ''}>{p.titulo || (p.cvus || []).join(', ')}</span>
                        <Badge className={p.waya === 'ok' ? 'bg-emerald-600 text-white' : p.waya === 'error' ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-600'}>Waya: {p.waya}</Badge>
                        <Badge className={p.email === 'ok' ? 'bg-emerald-600 text-white' : p.email === 'error' ? 'bg-red-600 text-white' : 'bg-slate-300 text-slate-700'} title={p.email_motivo || ''}>
                          {p.email === 'ok' ? 'Email enviado' : p.email === 'sin_email' ? 'Sin email' : p.email === 'error' ? 'Email error' : 'Email…'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                {bloqJob.estado === 'completado' && fallos.length > 0 && (
                  <div className="mt-2 text-[11px] border-t border-slate-200 pt-2 text-slate-500">
                    {fallos.map((e, i) => (<div key={i}>• {e.titulo}: <span className="text-red-600">{e.motivo}</span></div>))}
                  </div>
                )}
              </Card>
            );
          })()}
          <Card className="p-3 bg-white mb-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                <button onClick={() => setAlertaVista('detalle')} data-testid="waya-vista-detalle" className={`text-[11px] px-2.5 py-1 font-medium inline-flex items-center gap-1 ${alertaVista === 'detalle' ? 'bg-orange-600 text-white' : 'bg-white text-slate-500'}`}><List size={12} /> Detalle</button>
                <button onClick={() => setAlertaVista('muro')} data-testid="waya-vista-muro" className={`text-[11px] px-2.5 py-1 font-medium inline-flex items-center gap-1 ${alertaVista === 'muro' ? 'bg-orange-600 text-white' : 'bg-white text-slate-500'}`}><LayoutGrid size={12} /> Muro</button>
                <button onClick={() => setAlertaVista('agrupado')} data-testid="waya-vista-agrupado" className={`text-[11px] px-2.5 py-1 font-medium inline-flex items-center gap-1 ${alertaVista === 'agrupado' ? 'bg-orange-600 text-white' : 'bg-white text-slate-500'}`}><Layers size={12} /> Agrupado</button>
              </div>
              <div className="w-px h-5 bg-slate-200 mx-1" />
              {ESTADOS.map(e => (
                <button key={e.key} onClick={() => setEstadoFiltro(e.key)} data-testid={`waya-estado-${e.key}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition ${estadoFiltro === e.key ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>{e.label}</button>
              ))}
              <div className="w-px h-5 bg-slate-200 mx-1" />
              <div className="inline-flex items-center gap-1" data-testid="waya-rango-chips">
                <Clock size={13} className="text-slate-400" />
                {[{ h: 0, l: 'Todo' }, { h: 24, l: '24h' }, { h: 48, l: '48h' }, { h: 168, l: '7 días' }].map(r => (
                  <button key={r.h} onClick={() => setRangoHoras(r.h)} data-testid={`waya-rango-${r.h}`}
                    className={`text-[11px] px-2 py-1 rounded-full border font-medium transition ${rangoHoras === r.h ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>{r.l}</button>
                ))}
              </div>
              <Button variant="ghost" size="sm" onClick={() => (alertaVista === 'agrupado' || alertaVista === 'muro') ? fetchGrupos() : fetchBandeja()} data-testid="waya-refresh-btn" className="ml-auto">
                <RefreshCw size={14} className={(bandejaLoading || gruposLoading) ? 'animate-spin' : ''} />
              </Button>
            </div>

            {/* Toolbar detalle */}
            {alertaVista === 'detalle' && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t">
                {BANDEJA_CODES.map(c => (
                  <button key={c} onClick={() => toggleTipo(c)} data-testid={`waya-filtro-${c}`}
                    className={`text-[11px] px-2 py-1 rounded border font-medium transition ${filtroTipos.includes(c) ? ALERTA_META[c].cls + ' ring-2 ring-offset-1 ring-slate-300' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
                    {ALERTA_META[c].label} {bandeja.resumen_alertas?.[c] ? `(${bandeja.resumen_alertas[c]})` : ''}
                  </button>
                ))}
                <div className="w-px h-5 bg-slate-200 mx-1" />
                {['TODOS', 'INGRESO', 'EGRESO'].map(d => (
                  <button key={d} onClick={() => setFiltroDir(d)} data-testid={`waya-dir-${d}`}
                    className={`text-[11px] px-2 py-1 rounded border font-medium transition ${filtroDir === d ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>{d === 'TODOS' ? 'Todos' : d}</button>
                ))}
                <div className="ml-auto"><ColumnPicker catalog={ALERTA_COLS} visible={alertaCols} onToggle={toggleCol(setAlertaCols)} testid="waya-cols-alertas" /></div>
              </div>
            )}
            {/* Toolbar agrupado / muro */}
            {(alertaVista === 'agrupado' || alertaVista === 'muro') && (
              <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t">
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                  <button onClick={() => setAgrupadoPor('cvu')} data-testid="waya-por-cvu" className={`text-[11px] px-2.5 py-1 font-medium ${agrupadoPor === 'cvu' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500'}`}>Por cuenta (CVU)</button>
                  <button onClick={() => setAgrupadoPor('cuit')} data-testid="waya-por-cuit" className={`text-[11px] px-2.5 py-1 font-medium ${agrupadoPor === 'cuit' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500'}`}>Por persona (CUIT)</button>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-slate-500">
                  <AlertTriangle size={13} className="text-red-500" /> Resaltar ≥
                  <Input type="number" value={umbralMov} onChange={e => setUmbralMov(Number(e.target.value) || 0)} className="h-7 w-16 text-xs" data-testid="waya-umbral-mov" /> mov · monto ≥
                  <Input type="number" value={umbralMonto} onChange={e => setUmbralMonto(Number(e.target.value) || 0)} className="h-7 w-28 text-xs" data-testid="waya-umbral-monto" />
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  {perm.ejecutar && (<>
                  <Button size="sm" variant="outline" onClick={detectarNuevas} disabled={nuevasLoading}
                    className="h-8 text-xs bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-100" data-testid="waya-detectar-nuevas-btn">
                    {nuevasLoading ? <Loader2 size={13} className="mr-1 animate-spin" /> : <UserPlus size={13} className="mr-1" />} Detectar cuentas nuevas
                  </Button>
                  <Button size="sm" variant="outline" onClick={barridaInicial} disabled={nuevasLoading}
                    className="h-8 text-xs" data-testid="waya-barrida-inicial-btn" title="Acomoda el padrón: busca en Waya todas las cuentas vistas en movimientos que no estén registradas">
                    Barrida inicial del padrón
                  </Button>
                  <Button size="sm" variant="outline" onClick={igualarFechasAlta} disabled={nuevasLoading}
                    className="h-8 text-xs" data-testid="waya-backfill-fecha-btn" title="Puesta al día por única vez: completa la fecha de creación en las cuentas que ya tienen fecha de alta registrada">
                    Igualar fechas de alta
                  </Button>
                  </>)}
                  {alertaVista === 'agrupado' && perm.crear && (
                    <Button size="sm" onClick={() => agregarPadron(selGruposWaya)} disabled={padronLoading || selGruposWaya.length === 0}
                      className="bg-sky-600 hover:bg-sky-700 text-white h-8 text-xs" data-testid="waya-agregar-padron-btn">
                      {padronLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <UserPlus size={14} className="mr-1.5" />} Agregar al padrón ({selGruposWaya.length})
                    </Button>
                  )}
                  {alertaVista === 'agrupado' && selGrupos.size > 0 && (
                    <>
                      {perm.crear && (
                      <Button size="sm" onClick={generarDesdeSelGrupos}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid="waya-grupo-generar-btn">
                        <FolderPlus size={14} className="mr-1.5" /> Generar caso(s) ({selGrupos.size})
                      </Button>
                      )}
                      {perm.ejecutar && (
                      <Button size="sm" onClick={() => abrirBloqueo(idsFromGrupos(gruposOrdenados, selGrupos, 'cvu_mov'))}
                        className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs" data-testid="waya-grupo-bloquear-btn">
                        <Lock size={14} className="mr-1.5" /> Bloquear ({selGrupos.size})
                      </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {/* Acciones detalle PENDIENTE */}
            {alertaVista === 'detalle' && estadoFiltro === 'PENDIENTE' && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                {perm.crear && (
                <Button size="sm" onClick={abrirGenerar} disabled={seleccion.size === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="waya-generar-caso-btn"><FolderPlus size={14} className="mr-1.5" /> Generar caso ({seleccion.size})</Button>
                )}
                {perm.ejecutar && (
                <Button size="sm" onClick={() => abrirBloqueo(Array.from(seleccion))} disabled={seleccion.size === 0} className="bg-red-600 hover:bg-red-700 text-white" data-testid="waya-bloquear-btn"><Lock size={14} className="mr-1.5" /> Bloquear ({seleccion.size})</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => resolver('trabajar')} disabled={seleccion.size === 0} data-testid="waya-trabajar-btn"><ClipboardCheck size={14} className="mr-1.5" /> Marcar trabajada</Button>
                <Button size="sm" onClick={() => resolver('desestimar')} disabled={seleccion.size === 0} className="bg-red-600 hover:bg-red-700 text-white" data-testid="waya-desestimar-btn"><Ban size={14} className="mr-1.5" /> Desestimar ({seleccion.size})</Button>
              </div>
            )}
          </Card>

          {/* Tabla detalle */}
          {alertaVista === 'detalle' && (bandejaFiltrada.length === 0 ? (
            <Card className="p-10 bg-white text-center text-slate-400" data-testid="waya-alertas-empty">
              <Bell size={36} className="mx-auto text-slate-300 mb-2" /><p className="text-sm">No hay alertas con estos filtros.</p>
            </Card>
          ) : (
            <Card className="bg-white overflow-hidden" data-testid="waya-alertas-tabla">
              <div className="overflow-x-auto max-h-[55vh]">
                <table className="w-full text-xs">
                  <thead className="bg-orange-50 text-orange-700 uppercase text-[10px] sticky top-0">
                    <tr>
                      {estadoFiltro === 'PENDIENTE' && <th className="px-3 py-2 w-8"><Checkbox checked={bandejaFiltrada.length > 0 && bandejaFiltrada.every(m => seleccion.has(m.id_waya))} onCheckedChange={toggleSelAll} data-testid="waya-sel-all" /></th>}
                      {alertaVisible.map(c => <th key={c.key} className={`px-3 py-2 font-semibold whitespace-nowrap ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}
                      {estadoFiltro !== 'PENDIENTE' && <th className="text-left px-3 py-2 font-semibold">Caso</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {bandejaFiltrada.map((m, idx) => (
                      <tr key={`${m.id_waya}-${idx}`} className={`hover:bg-orange-50/40 ${seleccion.has(m.id_waya) ? 'bg-orange-50/60' : ''}`} data-testid={`waya-alerta-row-${idx}`}>
                        {estadoFiltro === 'PENDIENTE' && <td className="px-3 py-2"><Checkbox checked={seleccion.has(m.id_waya)} onCheckedChange={() => toggleSel(m.id_waya)} data-testid={`waya-alerta-check-${idx}`} /></td>}
                        {alertaVisible.map(c => <td key={c.key} className={`px-3 py-2 text-slate-700 ${c.align === 'right' ? 'text-right font-semibold whitespace-nowrap' : ''}`}>{c.render(m)}</td>)}
                        {estadoFiltro !== 'PENDIENTE' && <td className="px-3 py-2">{m.caso_id ? <button onClick={() => navigate(`/m/gestion-de-casos/caso/${m.caso_id}`)} className="text-emerald-600 hover:underline inline-flex items-center gap-1" data-testid={`waya-caso-link-${idx}`}>Ver caso <ExternalLink size={11} /></button> : <span className="text-slate-300">—</span>}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}

          {/* Tabla agrupado */}
          {alertaVista === 'agrupado' && (gruposOrdenados.length === 0 ? (
            <Card className="p-10 bg-white text-center text-slate-400" data-testid="waya-agrupado-empty">
              <Layers size={36} className="mx-auto text-slate-300 mb-2" /><p className="text-sm">No hay datos agrupados con estos filtros.</p>
            </Card>
          ) : (
            <Card className="bg-white overflow-hidden" data-testid="waya-agrupado-tabla">
              <div className="overflow-x-auto max-h-[55vh]">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 w-8"></th>
                      <th className="text-left px-3 py-2 font-semibold">{agrupadoPor === 'cuit' ? 'Persona (CUIT)' : 'Cuenta (CVU)'}</th>
                      <SortTh label="Alertados" col="alertados_count" orden={orden} onSort={onSort} align="right" />
                      <SortTh label="Monto alert." col="alertados_monto" orden={orden} onSort={onSort} align="right" />
                      <SortTh label="Total en base" col="total_count" orden={orden} onSort={onSort} align="right" />
                      <SortTh label="Monto total" col="total_monto" orden={orden} onSort={onSort} align="right" />
                      <th className="text-left px-3 py-2 font-semibold">Tipos de alerta</th>
                      <th className="text-left px-3 py-2 font-semibold">Última</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {gruposOrdenados.map((g, idx) => {
                      const susp = esSospechoso(g);
                      const esWaya = digits(g.cvu_mov).startsWith(WAYA_PREFIX);
                      return (
                        <tr key={`${g.clave}-${idx}`} className={`hover:bg-slate-50 ${susp ? 'bg-red-50/60' : ''}`} data-testid={`waya-grupo-row-${idx}`}>
                          <td className="px-3 py-2"><Checkbox checked={selGrupos.has(g.cvu_mov)} onCheckedChange={() => toggleSelGrupo(g.cvu_mov)} disabled={!esWaya} data-testid={`waya-grupo-check-${idx}`} /></td>
                          <td className="px-3 py-2 text-slate-700">
                            <div className="flex items-center gap-1">{g.nombre || '-'}{susp && <AlertTriangle size={12} className="text-red-500" />}{g.excluida && <ExcluidaTag testid={`waya-grupo-excluida-${idx}`} />}</div>
                            <div className="text-[10px] text-slate-400 font-mono">{agrupadoPor === 'cuit' ? (g.cuit || g.clave) : (g.cvu_mov || g.clave)}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">{g.alertados_count}</td>
                          <td className="px-3 py-2 text-right text-slate-600">{fmtMoneda(g.alertados_monto)}</td>
                          <td className={`px-3 py-2 text-right font-bold ${susp ? 'text-red-600' : 'text-slate-800'}`}>{g.total_count}</td>
                          <td className="px-3 py-2 text-right text-slate-600">{fmtMoneda(g.total_monto)}</td>
                          <td className="px-3 py-2"><TipoBadges g={g} /></td>
                          <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtFecha(g.ultima)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
          {/* Muro / Wall: tarjetas por cliente, con sección de propuestas del motor arriba */}
          {alertaVista === 'muro' && (() => {
            const conProp = gruposMuro.filter(g => propuestas[g.cvu_mov]);
            const resto = gruposMuro.filter(g => !propuestas[g.cvu_mov]);
            const card = (g, idx) => {
              const susp = esSospechoso(g);
              const esWaya = digits(g.cvu_mov).startsWith(WAYA_PREFIX);
              const esNueva = g.es_cuenta_nueva != null ? g.es_cuenta_nueva : !!(g.tipos && g.tipos.CUENTA_NUEVA);
              const noEnPadron = g.en_padron != null ? !g.en_padron : esNueva;
              const ids = (g.id_wayas || []).filter(Boolean);
              const prop = propuestas[g.cvu_mov];
              const motorCls = prop
                ? (prop.modo === 'EJECUTAR'
                    ? 'border-2 border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200 shadow-md shadow-indigo-100'
                    : 'border-2 border-sky-500 bg-sky-50 ring-2 ring-sky-200 shadow-md shadow-sky-100')
                : (susp ? 'border-red-300 ring-1 ring-red-200' : 'border-slate-200');
              return (
                  <Card key={`${g.clave}-${idx}`} data-testid={`waya-muro-card-${idx}`}
                    onClick={() => abrirMovModal(g)} title="Ver movimientos de la cuenta"
                    className={`p-4 bg-white border transition hover:shadow-lg cursor-pointer ${g.excluida ? 'border-2 border-slate-400 bg-slate-50' : motorCls}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5" title={g.nombre || ''}>
                          {g.nombre || 'Sin nombre'}
                          {esNueva && <Badge className="bg-fuchsia-100 text-fuchsia-700 text-[9px] px-1.5">NUEVA{g.dias_antiguedad != null ? ` · ${g.dias_antiguedad}d` : ''}</Badge>}
                          {g.excluida && <Badge className="bg-slate-200 text-slate-600 text-[9px] px-1.5">EXCLUIDA</Badge>}
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono truncate">CUIT: {g.cuit || '—'}</div>
                        <div className="text-[10px] text-slate-400 font-mono truncate">CVU: {g.cvu_mov || g.clave}</div>
                      </div>
                      <div className={`flex flex-col items-center justify-center rounded-lg px-3 py-1.5 shrink-0 ${susp ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`} data-testid={`waya-muro-count-${idx}`}>
                        <span className="text-2xl font-bold leading-none">{g.alertados_count}</span>
                        <span className="text-[9px] uppercase font-medium tracking-wide">Movimientos alertados</span>
                      </div>
                    </div>

                    <div className="mt-3">
                      <TipoBadges g={g} />
                    </div>

                    {g.excluida && (
                      <div className="mt-2 flex items-center gap-1.5 rounded-md px-2 py-1 bg-slate-700 text-white" data-testid={`waya-muro-excluida-${idx}`}>
                        <ShieldOff size={13} className="shrink-0" />
                        <span className="text-[11px] truncate"><b>EXCLUIDA</b> · el motor no la bloquea automáticamente</span>
                      </div>
                    )}

                    {prop && (
                      <div className={`mt-2 flex items-center gap-1.5 rounded-md px-2 py-1 ${prop.modo === 'EJECUTAR' ? 'bg-indigo-600 text-white' : 'bg-sky-600 text-white'}`} data-testid={`waya-muro-motor-${idx}`}>
                        <Bot size={13} className="shrink-0" />
                        <span className="text-[11px] truncate" title={(prop.reglas || []).join(', ')}>
                          Motor: <b>{prop.accion === 'CIERRE' ? 'Cerraría' : 'Bloquearía'}</b>
                          {prop.modo === 'EJECUTAR' ? ' · automático' : ' · propuesta'}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-3 text-[11px] text-slate-500">
                      <span>En base: <strong className={susp ? 'text-red-600' : 'text-slate-700'}>{g.total_count}</strong> mov</span>
                      <span className="font-semibold text-slate-700">{fmtMoneda(g.alertados_monto)}</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Última: {fmtFecha(g.ultima)}</div>

                    <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t items-center" onClick={e => e.stopPropagation()}>
                      <label className="flex items-center gap-1 text-[11px] text-slate-500 mr-1 cursor-pointer">
                        <Checkbox checked={selMuro.has(g.clave)} onCheckedChange={() => toggleSelMuro(g.clave)} data-testid={`waya-muro-check-${idx}`} /> Sel.
                      </label>
                      {perm.crear && (
                      <Button size="sm" onClick={() => generarDesdeGrupo(g)} disabled={!ids.length}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid={`waya-muro-generar-${idx}`}>
                        <FolderPlus size={13} className="mr-1" /> Generar caso
                      </Button>
                      )}
                      {perm.ejecutar && (
                      <Button size="sm" variant="outline" onClick={() => abrirBloqueo(ids)} disabled={!ids.length}
                        className="h-8 text-xs bg-red-50 text-red-700 border-red-200 hover:bg-red-100" data-testid={`waya-muro-bloquear-${idx}`}>
                        <Lock size={13} className="mr-1" /> Bloquear
                      </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => resolverIds(ids, 'desestimar')} disabled={!ids.length}
                        className="h-8 text-xs bg-red-50 text-red-700 border-red-200 hover:bg-red-100" data-testid={`waya-muro-desestimar-${idx}`}>
                        <Ban size={13} className="mr-1" /> Desestimar
                      </Button>
                      {esWaya && noEnPadron && (
                        <Button size="sm" variant="outline" onClick={() => agregarPadron([g.cvu_mov])} disabled={padronLoading}
                          className="h-8 text-xs bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100" data-testid={`waya-muro-padron-${idx}`}>
                          {padronLoading ? <Loader2 size={13} className="mr-1 animate-spin" /> : <UserPlus size={13} className="mr-1" />} Padrón
                        </Button>
                      )}
                      {perm.admin && (
                        <Button size="sm" variant="outline" onClick={() => excluirCuentas([{ cvu: g.cvu_mov, cuit: g.cuit, nombre: g.nombre }])}
                          className="h-8 text-xs bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100" data-testid={`waya-muro-excluir-${idx}`}
                          title="Excluir esta cuenta del monitoreo y del motor (por CVU y CUIT)">
                          <ShieldOff size={13} className="mr-1" /> Excluir
                        </Button>
                      )}
                      {prop && perm.admin && (
                        <Button size="sm" onClick={() => setMotorApply({ cvu: g.cvu_mov, verdict: prop, nombre: g.nombre })}
                          className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid={`waya-muro-motor-aplicar-${idx}`}>
                          <ShieldCheck size={13} className="mr-1" /> Aplicar motor
                        </Button>
                      )}
                    </div>
                  </Card>
              );
            };
            if (gruposMuro.length === 0) return (
              <Card className="p-10 bg-white text-center text-slate-400" data-testid="waya-muro-empty">
                <LayoutGrid size={36} className="mx-auto text-slate-300 mb-2" /><p className="text-sm">No hay cuentas con alertas para mostrar en el muro.</p>
              </Card>
            );
            return (
              <>
                {selMuro.size > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 bg-white rounded-md border shadow-sm sticky top-0 z-10" data-testid="waya-muro-bulk-bar">
                    <span className="text-xs text-slate-600 font-medium">{selMuro.size} cuenta(s) seleccionada(s)</span>
                    {perm.crear && (<Button size="sm" onClick={generarDesdeSeleccionMuro} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid="waya-muro-bulk-generar"><FolderPlus size={14} className="mr-1.5" /> Generar caso(s)</Button>)}
                    {perm.ejecutar && (<Button size="sm" onClick={() => abrirBloqueo(idsFromMuro())} className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs" data-testid="waya-muro-bulk-bloquear"><Lock size={14} className="mr-1.5" /> Bloquear</Button>)}
                    {perm.admin && (<Button size="sm" variant="outline" onClick={() => excluirCuentas(gruposMuro.filter(g => selMuro.has(g.clave)).map(g => ({ cvu: g.cvu_mov, cuit: g.cuit, nombre: g.nombre })))} className="bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 h-8 text-xs" data-testid="waya-muro-bulk-excluir"><ShieldOff size={14} className="mr-1.5" /> Excluir seleccionadas</Button>)}
                    <Button size="sm" variant="ghost" onClick={() => setSelMuro(new Set())} className="h-8 text-xs" data-testid="waya-muro-bulk-clear">Limpiar selección</Button>
                  </div>
                )}
                {(excluidasOcultas > 0 || mostrarExcluidas) && (
                  <div className="flex items-center gap-2 mb-3 text-xs text-slate-500">
                    <label className="flex items-center gap-1.5 cursor-pointer" data-testid="waya-muro-toggle-excluidas">
                      <Checkbox checked={mostrarExcluidas} onCheckedChange={(v) => setMostrarExcluidas(!!v)} /> Mostrar cuentas excluidas
                    </label>
                    {!mostrarExcluidas && excluidasOcultas > 0 && <span className="text-slate-400">({excluidasOcultas} oculta(s) por exclusión)</span>}
                  </div>
                )}
                {conProp.length > 0 && (
                  <div className="mb-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Bot size={15} className="text-sky-600" />
                      <h3 className="text-sm font-semibold text-slate-700">Con propuesta del motor ({conProp.length})</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="waya-muro-grid-motor">
                      {conProp.map((g, i) => card(g, i))}
                    </div>
                  </div>
                )}
                <div>
                  {conProp.length > 0 && (
                    <div className="flex items-center gap-2 mb-2">
                      <List size={15} className="text-slate-400" />
                      <h3 className="text-sm font-semibold text-slate-500">Otras cuentas ({resto.length})</h3>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="waya-muro-grid">
                    {resto.map((g, i) => card(g, conProp.length + i))}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Historial */}
      {tab === 'historial' && (
        <div data-testid="waya-historial-section">
          <div className="flex justify-end mb-2">
            <Button variant="ghost" size="sm" onClick={fetchHistorial} data-testid="waya-historial-refresh"><RefreshCw size={14} className={historialLoading ? 'animate-spin' : ''} /></Button>
          </div>
          {historial.length === 0 ? (
            <Card className="p-10 bg-white text-center text-slate-400" data-testid="waya-historial-empty">
              <History size={36} className="mx-auto text-slate-300 mb-2" /><p className="text-sm">Todavía no hay corridas registradas.</p>
            </Card>
          ) : (
            <Card className="bg-white overflow-hidden" data-testid="waya-historial-tabla">
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                      <th className="text-left px-3 py-2 font-semibold">Tipo</th>
                      <th className="text-left px-3 py-2 font-semibold">Usuario</th>
                      <th className="text-left px-3 py-2 font-semibold">Rango</th>
                      <th className="text-left px-3 py-2 font-semibold">Estado</th>
                      <th className="text-right px-3 py-2 font-semibold">Extraídos</th>
                      <th className="text-right px-3 py-2 font-semibold">Alertados</th>
                      <th className="text-right px-3 py-2 font-semibold">Guardados / Ya existían</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {historial.map((h, idx) => (
                      <tr key={h.id || idx} className="hover:bg-slate-50" data-testid={`waya-historial-row-${idx}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtFecha(h.fecha)}</td>
                        <td className="px-3 py-2"><Badge className={h.tipo === 'auto' ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}>{h.tipo === 'auto' ? 'Automática' : 'Manual'}</Badge></td>
                        <td className="px-3 py-2 text-slate-600">{h.usuario || 'sistema'}</td>
                        <td className="px-3 py-2 text-[10px] text-slate-500 whitespace-nowrap">{h.rango_desde} → {h.rango_hasta}</td>
                        <td className="px-3 py-2">{h.estado === 'ok' ? <Badge className="bg-emerald-100 text-emerald-700">OK</Badge> : <Badge className="bg-red-100 text-red-700" title={h.error}>Error</Badge>}</td>
                        <td className="px-3 py-2 text-right">{h.extraidos ?? '-'}</td>
                        <td className="px-3 py-2 text-right">{h.alertadas ?? '-'}</td>
                        <td className="px-3 py-2 text-right text-slate-600">{h.guardados ?? 0} / {h.ya_existian ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Dialog generar caso */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="waya-generar-dialog">
          <DialogHeader>
            <DialogTitle>Generar caso desde alertas</DialogTitle>
            <DialogDescription>{seleccion.size} alerta(s) seleccionada(s). Elegí cómo agruparlas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <RadioGroup value={genAgrup} onValueChange={setGenAgrup}>
              <div className="flex items-start gap-2"><RadioGroupItem value="cvu" id="agrup-cvu" data-testid="waya-agrup-cvu" className="mt-1" /><Label htmlFor="agrup-cvu" className="font-normal cursor-pointer"><span className="font-medium">1 caso por cuenta (cvu_mov)</span><span className="block text-xs text-slate-500">Agrupa todos los movimientos alertados de cada cuenta.</span></Label></div>
              <div className="flex items-start gap-2"><RadioGroupItem value="uno" id="agrup-uno" data-testid="waya-agrup-uno" className="mt-1" /><Label htmlFor="agrup-uno" className="font-normal cursor-pointer"><span className="font-medium">1 caso agrupando todas las seleccionadas</span><span className="block text-xs text-slate-500">Un único caso con todas las cuentas.</span></Label></div>
            </RadioGroup>
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2.5" data-testid="waya-arrastrar-wrap">
              <Checkbox id="arrastrar" checked={genArrastrar} onCheckedChange={(v) => setGenArrastrar(!!v)} data-testid="waya-arrastrar-check" className="mt-0.5" />
              <Label htmlFor="arrastrar" className="font-normal cursor-pointer">
                <span className="font-medium text-emerald-800">Arrastrar toda la cuenta</span>
                <span className="block text-xs text-emerald-700">Incluye automáticamente TODAS las alertas pendientes de las mismas cuentas, aunque no las hayas marcado.</span>
              </Label>
            </div>
            {genAgrup === 'uno' && <div><Label>Título del caso (opcional)</Label><Input value={genTitulo} onChange={e => setGenTitulo(e.target.value)} placeholder="Monitor Waya - alertas agrupadas" className="mt-1" data-testid="waya-gen-titulo" /></div>}
            <div><Label>Prioridad</Label>
              <Select value={genPrioridad} onValueChange={setGenPrioridad}>
                <SelectTrigger className="mt-1" data-testid="waya-gen-prioridad"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ALTA">Alta</SelectItem><SelectItem value="MEDIA">Media</SelectItem><SelectItem value="BAJA">Baja</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)} disabled={genLoading}>Cancelar</Button>
            <Button onClick={confirmarGenerar} disabled={genLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="waya-gen-confirm">
              {genLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FolderPlus size={16} className="mr-2" />} Crear caso(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog bloqueo / cierre definitivo */}
      <Dialog open={bloqOpen} onOpenChange={setBloqOpen}>
        <DialogContent data-testid="waya-bloqueo-dialog">
          <DialogHeader>
            <DialogTitle>Bloquear cuenta(s)</DialogTitle>
            <DialogDescription>Se creará el caso, se ejecutará la acción en Waya y se enviará el email de notificación (si no hay correo en base, se busca en Waya).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <RadioGroup value={bloqTipo} onValueChange={setBloqTipo}>
              <div className="flex items-start gap-2"><RadioGroupItem value="preventivo" id="bloq-prev" data-testid="waya-bloq-preventivo" className="mt-1" /><Label htmlFor="bloq-prev" className="font-normal cursor-pointer"><span className="font-medium">Bloqueo preventivo</span><span className="block text-xs text-slate-500">Bloquea la cuenta en Waya. El caso queda en <strong>Esperando Compliance</strong>.</span></Label></div>
              <div className="flex items-start gap-2"><RadioGroupItem value="definitivo" id="bloq-def" data-testid="waya-bloq-definitivo" className="mt-1" /><Label htmlFor="bloq-def" className="font-normal cursor-pointer"><span className="font-medium">Cierre definitivo</span><span className="block text-xs text-slate-500">Da de baja el CVU en Waya. El caso queda <strong>Cerrado</strong>.</span></Label></div>
            </RadioGroup>
            <div><Label>Prioridad</Label>
              <Select value={bloqPrioridad} onValueChange={setBloqPrioridad}>
                <SelectTrigger className="mt-1" data-testid="waya-bloq-prioridad"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ALTA">Alta</SelectItem><SelectItem value="MEDIA">Media</SelectItem><SelectItem value="BAJA">Baja</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="rounded-md bg-blue-50 border border-blue-200 p-2.5 text-xs text-blue-800 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> La acción en Waya (bloqueo/baja) y el envío del email corren en segundo plano; te aviso cuando termine.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBloqOpen(false)} disabled={bloqLoading}>Cancelar</Button>
            <Button onClick={confirmarBloqueo} disabled={bloqLoading}
              className={bloqTipo === 'definitivo' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} data-testid="waya-bloq-confirm">
              {bloqLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Lock size={16} className="mr-2" />} {bloqTipo === 'definitivo' ? 'Cerrar definitivamente' : 'Bloquear preventivamente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación de "Aplicar motor" desde el Muro */}
      <Dialog open={!!motorApply} onOpenChange={(o) => { if (!o && !motorLoading) setMotorApply(null); }}>
        <DialogContent className="max-w-md" data-testid="waya-muro-motor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Bot size={18} /> Aplicar propuesta del motor</DialogTitle>
            <DialogDescription>
              El motor propone {motorApply?.verdict?.accion === 'CIERRE'
                ? <b> cierre definitivo (baja de CVU)</b>
                : <b> bloqueo preventivo</b>} para la cuenta <b>{motorApply?.nombre || motorApply?.cvu}</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <div className="text-xs text-slate-600">
              Regla(s): {(motorApply?.verdict?.reglas || []).join(', ') || '—'}
            </div>
            <div className="text-xs text-red-800 bg-red-100 border border-red-200 rounded-md p-2 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Se creará el caso, se ejecutará la acción en Waya y se enviará el email.
              {motorApply?.verdict?.accion === 'CIERRE' && <> El cierre definitivo <b>no se puede deshacer</b>.</>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMotorApply(null)} disabled={motorLoading} data-testid="waya-muro-motor-cancel">Cancelar</Button>
            <Button onClick={aplicarMotor} disabled={motorLoading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="waya-muro-motor-confirm">
              {motorLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ShieldCheck size={16} className="mr-2" />} Sí, aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal "Ver movimientos" de una cuenta (Muro) */}
      <Dialog open={movModalOpen} onOpenChange={setMovModalOpen}>
        <DialogContent className="max-w-4xl" data-testid="waya-mov-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List size={18} /> Movimientos de {movModalGrupo?.nombre || 'la cuenta'}
            </DialogTitle>
            <DialogDescription>
              CVU {movModalGrupo?.cvu_mov || '—'} · {movModalItems.length} movimiento(s) alertado(s) ({ESTADOS.find(e => e.key === estadoFiltro)?.label || estadoFiltro})
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-auto rounded-md border">
            {movModalLoading ? (
              <div className="p-10 text-center text-slate-400"><Loader2 size={28} className="mx-auto animate-spin mb-2" /> Cargando…</div>
            ) : movModalItems.length === 0 ? (
              <div className="p-10 text-center text-slate-400" data-testid="waya-mov-modal-empty"><Bell size={28} className="mx-auto text-slate-300 mb-2" /> Sin movimientos alertados con el filtro actual.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                  <tr>
                    <th className="px-3 py-2 w-8"><Checkbox checked={movModalItems.length > 0 && movModalItems.every(m => movModalSel.has(m.id_waya))} onCheckedChange={toggleMovSelAll} data-testid="waya-mov-modal-sel-all" /></th>
                    <th className="text-left px-3 py-2 font-semibold">Movimiento</th>
                    <th className="text-right px-3 py-2 font-semibold">Monto</th>
                    <th className="text-left px-3 py-2 font-semibold">Fecha mov.</th>
                    <th className="text-left px-3 py-2 font-semibold">Contraparte</th>
                    <th className="text-left px-3 py-2 font-semibold">Alertas</th>
                    <th className="text-left px-3 py-2 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {movModalItems.map((m, i) => (
                    <tr key={`${m.id_waya}-${i}`} className={`hover:bg-orange-50/40 ${movModalSel.has(m.id_waya) ? 'bg-orange-50/60' : ''}`} data-testid={`waya-mov-modal-row-${i}`}>
                      <td className="px-3 py-2"><Checkbox checked={movModalSel.has(m.id_waya)} onCheckedChange={() => toggleMovSel(m.id_waya)} data-testid={`waya-mov-modal-check-${i}`} /></td>
                      <td className="px-3 py-2"><MovBadge tipo={m.movimiento} /></td>
                      <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{fmtMoneda(m.monto)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">{m.fecha}</td>
                      <td className="px-3 py-2 text-slate-600">{m.movimiento === 'EGRESO' ? (m.nombre_destino || '-') : (m.nombre_origen || '-')}</td>
                      <td className="px-3 py-2"><AlertasCelda m={m} /></td>
                      <td className="px-3 py-2"><Badge className={ESTADO_CLS[m.estado] || ''}>{m.estado}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2 sm:justify-start">
            <span className="text-xs text-slate-500 mr-auto self-center">{movModalSel.size} seleccionado(s)</span>
            {perm.crear && (
            <Button size="sm" onClick={modalGenerar} disabled={movModalSel.size === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="waya-mov-modal-generar"><FolderPlus size={14} className="mr-1.5" /> Generar caso</Button>
            )}
            {perm.ejecutar && (
            <Button size="sm" onClick={modalBloquear} disabled={movModalSel.size === 0} className="bg-red-600 hover:bg-red-700 text-white" data-testid="waya-mov-modal-bloquear"><Lock size={14} className="mr-1.5" /> Bloquear</Button>
            )}
            <Button size="sm" variant="outline" onClick={modalDesestimar} disabled={movModalSel.size === 0} className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100" data-testid="waya-mov-modal-desestimar"><Ban size={14} className="mr-1.5" /> Desestimar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog admin: permisos de acciones por usuario */}
      <Dialog open={permDlgOpen} onOpenChange={setPermDlgOpen}>
        <DialogContent className="max-w-2xl" data-testid="waya-permisos-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock size={18} /> Permisos de acciones · Monitor Waya</DialogTitle>
            <DialogDescription>Activá o desactivá por usuario las acciones del monitor. <strong>Crear</strong>: generar casos y agregar al padrón. <strong>Ejecutar</strong>: extraer, automatización, mantenimiento y bloquear. Los administradores siempre tienen acceso total.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-auto rounded-md border">
            {permLoading ? (
              <div className="p-8 text-center text-slate-400"><Loader2 size={24} className="mx-auto animate-spin" /></div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Usuario</th>
                    <th className="text-center px-3 py-2 font-semibold">Crear</th>
                    <th className="text-center px-3 py-2 font-semibold">Ejecutar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {permUsers.map(u => (
                    <tr key={u.id} data-testid={`waya-perm-row-${u.id}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800 flex items-center gap-1">{u.nombre}{u.es_admin && <Badge className="bg-slate-100 text-slate-600 text-[9px]">Admin</Badge>}</div>
                        <div className="text-[10px] text-slate-400">{u.email} · {u.rol}</div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Switch checked={u.crear} disabled={u.es_admin} onCheckedChange={(v) => togglePermUser(u, 'crear', v)} data-testid={`waya-perm-crear-${u.id}`} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Switch checked={u.ejecutar} disabled={u.es_admin} onCheckedChange={(v) => togglePermUser(u, 'ejecutar', v)} data-testid={`waya-perm-ejecutar-${u.id}`} />
                      </td>
                    </tr>
                  ))}
                  {permUsers.length === 0 && (<tr><td colSpan={3} className="p-6 text-center text-slate-400">Sin usuarios.</td></tr>)}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDlgOpen(false)} data-testid="waya-perm-cerrar">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WayaMovimientos;
